import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTeacher } from '@/context/TeacherContext';
import {
  Bot,
  User,
  LogOut,
  BookOpen,
  Sparkles,
  X,
  Calendar,
  Clock,
  Coins,
  FileText,
  Sun,
  Moon,
  Settings,
  PlaySquare
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { clearAuthTokens, getPortalType, getUserData, clearAllSessions, getAuthToken, performLogoutTeardown } from '@/utils/auth';
import { apiUrl, mediaUrl } from '@/api/config';
import axios from 'axios';

import { useUserProfile } from '@/context/UserProfileContext';
import { useCompliance } from '@/context/ComplianceContext';

export default function TeacherSidebar({
  activeSection,
  setActiveSection,
  className,
  onNavigate,
  showCloseButton = false,
  onClose = () => { },
  onLogout
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { teacherTheme, toggleTheme } = useTeacher();
  const { profile } = useUserProfile();
  const { show_subscriptions } = useCompliance();
  const userData = getUserData('teacher') || {};
  const isAmbassador = Boolean(profile?.is_affiliator || userData?.is_affiliator);
  const [liveTeacherProgramEnabled, setLiveTeacherProgramEnabled] = useState(null);

  useEffect(() => {
    const token = getAuthToken('teacher');
    if (!token) return;
    axios.get(apiUrl('/superadmin/affiliators/teacher-program-settings/'), {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => {
        if (res.data) setLiveTeacherProgramEnabled(Boolean(res.data.enabled));
      })
      .catch(() => {});
  }, []);

  const isProgramEnabled = Boolean(
    liveTeacherProgramEnabled !== null
      ? liveTeacherProgramEnabled
      : (profile?.teacher_ambassador_program_enabled ?? userData?.teacher_ambassador_program_enabled)
  );
  const isDark = teacherTheme === 'dark';

  const [isHovered, setIsHovered] = useState(false);
  const sidebarRef = useRef(null);

  const [isTutor, setIsTutor] = useState(() => {
    const userData = getUserData('teacher') || {};
    return Boolean(
      profile?.is_tutor_configured ??
      userData.is_tutor_configured ??
      profile?.is_tutor ??
      userData?.is_tutor ??
      profile?.tutor_id ??
      userData?.tutor_id ??
      (profile?.role?.toLowerCase() === 'tutor') ??
      (userData?.role?.toLowerCase() === 'tutor') ??
      false
    );
  });

  const userPhoto = profile?.profile_photo || profile?.profile_pic_url || profile?.photo || profile?.avatar || userData?.profile_photo || userData?.profile_pic_url || userData?.photo || userData?.avatar;
  const profilePicUrl = userPhoto ? (userPhoto.startsWith('http') ? userPhoto : mediaUrl(userPhoto)) : null;
  const fullName = profile?.full_name || userData?.full_name || `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || userData?.email?.split('@')[0] || 'Teacher';
  const roleName = userData?.role || (isTutor ? 'Tutor' : 'Teacher');

  // Collapse sidebar when clicking outside on desktop
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setIsHovered(false);
      }
    };
    document.addEventListener('pointerdown', handleClickOutside);
    return () => document.removeEventListener('pointerdown', handleClickOutside);
  }, []);

  useEffect(() => {
    let isMounted = true;
    const checkTutor = async () => {
      const userData = getUserData('teacher') || {};
      const configured = Boolean(
        profile?.is_tutor_configured ??
        userData.is_tutor_configured ??
        profile?.is_tutor ??
        userData?.is_tutor ??
        profile?.tutor_id ??
        userData?.tutor_id ??
        (profile?.role?.toLowerCase() === 'tutor') ??
        (userData?.role?.toLowerCase() === 'tutor') ??
        false
      );

      if (configured) {
        if (isMounted) setIsTutor(true);
        return;
      }

      // Fallback: check my_profile from API
      try {
        const token = getAuthToken('teacher');
        if (token) {
          const res = await axios.get(apiUrl('superadmin/tutors/my_profile/'), {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.data && (res.data.id || res.data.tutor_id || res.data.is_tutor_configured !== false)) {
            if (isMounted) setIsTutor(true);
          }
        }
      } catch (e) {
        // Not a tutor
      }
    };

    checkTutor();

    window.addEventListener('profileUpdated', checkTutor);
    window.addEventListener('login_success', checkTutor);
    window.addEventListener('storage', checkTutor);
    return () => {
      isMounted = false;
      window.removeEventListener('profileUpdated', checkTutor);
      window.removeEventListener('login_success', checkTutor);
      window.removeEventListener('storage', checkTutor);
    };
  }, [profile]);

  const hasInstitute = Boolean(profile?.institute || userData?.institute);
  const institute = profile?.institute || userData?.institute;
  const instituteName = institute?.name;

  const menuItems = [
    { id: 'teach', label: 'TEACH', icon: BookOpen, color: 'text-[#9AD5D0]', type: 'regular' },
    { id: 'trainings', label: 'TRAININGS', icon: PlaySquare, color: 'text-[#9AD5D0]', type: 'regular' },
    // Temporarily hidden per request:
    // { id: 'ai-tools', label: 'AI TOOLS', icon: Bot, color: 'text-[#C9A961]', type: 'regular' },
    // { id: 'notes', label: 'CLASS NOTES', icon: FileText, color: 'text-[#9AD5D0]', type: 'regular' },
    ...(isTutor ? [
      { id: 'coaching-sessions', label: 'MY SESSIONS', icon: Calendar, color: 'text-[#C9A961]', type: 'regular' },
      { id: 'earnings', label: 'MY EARNINGS', icon: Coins, color: 'text-[#9AD5D0]', type: 'regular' },
      { id: 'settings', label: 'MY AVAILABILITY', icon: Clock, color: 'text-[#C9A961]', type: 'regular' }
    ] : []),
    ...(isProgramEnabled && isAmbassador && show_subscriptions ? [
      { id: 'affiliate-earnings', label: 'AMBASSADOR EARNINGS', icon: Sparkles, color: 'text-amber-500', type: 'regular' }
    ] : []),
  ];

  const handleItemClick = (itemId) => {
    if (setActiveSection) {
      setActiveSection(itemId);
    } else {
      if (itemId === 'teach') {
        navigate('/teacher/teach');
      } else if (itemId === 'trainings') {
        navigate('/teacher/trainings');
      } else if (itemId === 'ai-tools') {
        navigate('/teacher/ai-tools');
      } else if (itemId === 'notes') {
        navigate('/teacher/notes');
      } else if (itemId === 'coaching-sessions') {
        navigate('/teacher/coaching-sessions');
      } else if (itemId === 'earnings') {
        navigate('/teacher/earnings');
      } else if (itemId === 'settings') {
        navigate('/teacher/settings');
      } else if (itemId === 'affiliate-earnings') {
        navigate('/teacher/affiliate-earnings');
      }
    }

    setIsHovered(false);
    if (onNavigate) onNavigate();
  };

  const handleLogoutClick = async () => {
    if (onLogout) {
      onLogout();
    } else {
      try {
        clearAuthTokens('teacher');
        localStorage.removeItem('fcm_token');
      } catch (e) {
        console.error(e);
      } finally {
        await performLogoutTeardown('/teacher/login');
      }
    }
    if (onNavigate) onNavigate();
  };

  const getActiveSection = () => {
    const pathname = location.pathname || '';
    if (pathname.includes('/coaching-sessions')) return 'coaching-sessions';
    if (pathname.includes('/trainings')) return 'trainings';
    if (pathname.includes('/earnings')) return 'earnings';
    if (pathname.includes('/settings')) return 'settings';
    if (pathname.includes('/ai-tools')) return 'ai-tools';
    if (pathname.includes('/notes')) return 'notes';
    if (pathname.includes('/profile')) return 'profile';
    if (pathname.includes('/teacher/teach') || pathname.endsWith('/teach') || pathname === '/teacher' || pathname === '/teacher/') return 'teach';
    return activeSection || 'teach';
  };
  const currentActive = getActiveSection();

  const canHover = !showCloseButton && !className?.includes('w-full');
  const isMobileDrawer = !canHover;
  const expanded = !canHover || isHovered;
  const compact = !expanded;

  return (
    <div
      ref={sidebarRef}
      className={cn(
        "h-[100dvh] border-r flex flex-col transition-all duration-300 ease-out z-40 pb-safe select-none",
        (showCloseButton || isMobileDrawer) && "pt-safe-top pl-safe-left",
        isDark ? "bg-[hsl(var(--sidebar-bg))] border-[hsl(var(--border))]" : "bg-[hsl(var(--sidebar-bg))] border-[hsl(var(--card-border))]",
        compact ? "w-16" : "w-64",
        className
      )}
      data-icon-only={compact ? "true" : "false"}
      onMouseEnter={canHover ? () => setIsHovered(true) : undefined}
      onMouseLeave={canHover ? () => setIsHovered(false) : undefined}
    >
      {/* Section 1: Logo + Brand Name - Theme Aware */}
      <div className={cn("border-b border-[hsl(var(--card-border))]/55 transition-all duration-300 relative flex items-center",
        isMobileDrawer
          ? "h-16 px-5"
          : expanded 
            ? "px-5 pt-6 pb-6" 
            : "p-3 pt-5 pb-5 justify-center",
        "bg-[hsl(var(--sidebar-bg))]"
      )}>
        <div className={cn("flex items-center w-full min-w-0", expanded ? "justify-between" : "justify-center")}>
          {expanded ? (
            <div className="flex-1 flex flex-col justify-center min-w-0">
              <img
                src="/logo_tagline_black.png"
                alt="Budlee AI"
                className="dark:hidden h-8 sm:h-8.5 object-contain w-auto max-w-[170px] sm:max-w-[190px]"
                onContextMenu={(e) => e.preventDefault()}
              />
              <img
                src="/logo_tagline_white.png"
                alt="Budlee AI"
                className="hidden dark:block h-8 sm:h-8.5 object-contain w-auto max-w-[170px] sm:max-w-[190px]"
                onContextMenu={(e) => e.preventDefault()}
              />
            </div>
          ) : (
            <img
              src="/logo.png"
              alt="Budlee AI Logo"
              className="h-10 w-10 sm:h-11 sm:w-11 object-contain flex-shrink-0 rounded-lg"
              onContextMenu={(e) => e.preventDefault()}
            />
          )}
        </div>
      </div>

      {/* Section 2: Menu Items - Main Navigation */}
      <nav className={cn("flex-1 min-h-0 overflow-y-auto space-y-1 transition-colors",
        expanded ? "px-3 py-4" : "px-2 py-4",
        isDark ? "bg-[hsl(var(--main-bg))]" : "bg-[#FAF8F5]"
      )}>
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive = currentActive === item.id;

          return (
            <button
              key={item.id}
              onClick={() => handleItemClick(item.id)}
              className={cn(
                'w-full flex items-center rounded-xl font-medium text-sm uppercase tracking-wide transition-all duration-300 relative overflow-hidden',
                expanded ? 'gap-4 px-4 py-3.5' : 'p-3 justify-center',
                isActive
                  ? isDark
                    ? 'bg-[hsl(var(--sidebar-hover))] text-[hsl(var(--foreground))] shadow-md'
                    : 'bg-[#F5F1EB] text-[#2B2B2B] shadow-md'
                  : isDark
                    ? 'text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--sidebar-hover))]/50 hover:text-[hsl(var(--foreground))]'
                    : 'text-[#2B2B2B] hover:bg-[#F5F1EB]/50 hover:text-black'
              )}
              title={item.label}
              aria-label={item.label}
            >
              {isActive && (
                <div className={cn("absolute inset-0 rounded-xl p-[2px] bg-gradient-to-r from-[#C9A961] via-[#D4AF77] to-[#C9A961] animate-pulse",
                  isDark && "from-[hsl(var(--gold-primary))] via-[hsl(var(--gold-secondary))] to-[hsl(var(--gold-primary))]"
                )}>
                  <div className={cn("w-full h-full bg-[#F5F1EB] rounded-xl",
                    isDark && "bg-[hsl(var(--sidebar-hover))]"
                  )} />
                </div>
              )}

              <div className={cn("relative z-10 flex items-center w-full", expanded ? "gap-4" : "justify-center")}>
                <Icon className={cn('w-5 h-5 flex-shrink-0', item.color)} />
                {expanded && <span className="flex-1 text-left">{item.label}</span>}
              </div>
            </button>
          );
        })}
      </nav>

      {/* Section 3: Profile, Role, Theme Toggle & Logout at the Bottom */}
      <div className={cn("border-t transition-colors",
        "border-[hsl(var(--card-border))] bg-[hsl(var(--sidebar-bg))]",
        compact ? "p-2" : "p-3"
      )}>
        {compact ? (
          <div className="flex flex-col items-center gap-2 shrink-0">
            <button
              onClick={toggleTheme}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--sidebar-hover))] transition-colors"
              title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
              aria-label={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
            >
              {isDark ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4" />}
            </button>
            <button
              onClick={() => {
                navigate('/teacher/profile');
                if (onNavigate) onNavigate();
              }}
              className="w-9 h-9 rounded-full bg-[hsl(var(--card-border))] flex items-center justify-center font-bold text-xs uppercase overflow-hidden shrink-0 border border-[hsl(var(--gold-primary))]/30 hover:opacity-80 transition-opacity"
              title="View Profile"
            >
              {profilePicUrl ? (
                <img
                  src={profilePicUrl}
                  alt={fullName}
                  className="w-full h-full object-cover rounded-full"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              ) : (
                <span className="text-[hsl(var(--gold-primary))]">
                  {fullName.substring(0, 2).toUpperCase()}
                </span>
              )}
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-1.5 px-1 py-1 bg-[hsl(var(--sidebar-hover))]/40 rounded-2xl border border-[hsl(var(--card-border))] shadow-sm">
            <button
              onClick={() => {
                setIsHovered(false);
                navigate('/teacher/profile');
                if (onNavigate) onNavigate();
              }}
              className="flex items-center gap-2 min-w-0 hover:opacity-80 transition-opacity cursor-pointer text-left flex-1"
              title="View Profile"
            >
              <div className="w-8 h-8 rounded-full bg-[hsl(var(--card-border))] flex items-center justify-center font-bold text-xs uppercase overflow-hidden shrink-0 border border-[hsl(var(--gold-primary))]/30">
                {profilePicUrl ? (
                  <img
                    src={profilePicUrl}
                    alt={fullName}
                    className="w-full h-full object-cover rounded-full"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                ) : (
                  <span className="text-[hsl(var(--gold-primary))]">
                    {fullName.substring(0, 2).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="min-w-0 flex-1 flex flex-col items-start text-left">
                <p className="text-xs font-bold truncate text-foreground leading-tight w-full text-left">
                  {fullName}
                </p>
                <p className="text-[9px] font-black text-[hsl(var(--gold-primary))] uppercase tracking-widest leading-none mt-0.5">
                  {roleName}
                </p>
                {instituteName && (
                  <span className="text-[8.5px] font-bold text-teal-500 uppercase tracking-wide truncate max-w-[120px] mt-0.5" title={instituteName}>
                    {instituteName}
                  </span>
                )}
              </div>
            </button>

            <div className="flex items-center gap-0.5 shrink-0">
              <button
                onClick={toggleTheme}
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--sidebar-hover))] transition-colors"
                title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
                aria-label={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
              >
                {isDark ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4" />}
              </button>
              <button
                onClick={handleLogoutClick}
                className="p-1.5 rounded-lg text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors"
                title="Logout"
                aria-label="Logout"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
